"use client";

import { ClockIcon } from "@phosphor-icons/react";
import { DateSegment } from "./DateSegment";
import { useRef } from "react";
import {
  AriaTimeFieldProps,
  TimeValue,
  useLocale,
  useTimeField,
} from "react-aria";
import { useTimeFieldState } from "react-stately";
import { cn } from "../../utils";

function TimeField(
  props: AriaTimeFieldProps<TimeValue> & {
    className?: string;
    hasError?: boolean;
  }
) {
  const ref = useRef<HTMLDivElement>(null);

  const { locale } = useLocale();
  const state = useTimeFieldState({
    ...props,
    locale,
  });
  const {
    fieldProps: { ...fieldProps },
  } = useTimeField(props, state, ref);

  // Clicking the icon focuses the first segment, like clicking into a text input.
  const focusFirstSegment = () => {
    ref.current?.querySelector<HTMLElement>('[role="spinbutton"]')?.focus();
  };

  return (
    <div
      {...fieldProps}
      ref={ref}
      className={cn(
        "border-input focus-within:ring-ring flex h-9 w-full items-center gap-2 rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-within:outline-none focus-within:ring-1",
        {
          "cursor-not-allowed opacity-50": props.isDisabled,
          "cursor-text": !props.isDisabled,
          "border-destructive focus-within:ring-destructive": props.hasError,
        },
        props.className
      )}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        disabled={props.isDisabled}
        onClick={focusFirstSegment}
        className="text-muted-foreground flex shrink-0 items-center"
      >
        <ClockIcon size={16} />
      </button>
      <div className="flex flex-1 items-center">
        {state.segments.map((segment, i) => (
          <DateSegment key={i} segment={segment} state={state} />
        ))}
      </div>
    </div>
  );
}

export { TimeField };
